import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { MigrationSyncLogService, MigrationSyncLogDto } from '../../../../api/gen';

@Component({
    selector: 'app-migration-sync-logs',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatTableModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatFormFieldModule,
        MatTooltipModule
    ],
    templateUrl: './migration-sync-logs.component.html',
    styleUrl: './migration-sync-logs.component.scss'
})
export class MigrationSyncLogsComponent implements OnInit {
    private migrationSyncLogService = inject(MigrationSyncLogService);

    logs = signal<MigrationSyncLogDto[]>([]);
    isLoading = signal(false);
    pageIndex = signal(0);
    pageSize = signal(10);
    totalCount = signal(0);
    statusFilter = signal<string | undefined>(undefined);

    displayedColumns = ['startedAt', 'completedAt', 'status', 'parkersCreated', 'licensePlates', 'orgsCreated', 'parkingRightsImported', 'errorMessage'];

    readonly statusOptions = ['Completed', 'Failed', 'Running'];

    ngOnInit(): void {
        this.loadLogs();
    }

    loadLogs(): void {
        this.isLoading.set(true);
        this.migrationSyncLogService.migrationSyncLogsGet(this.pageIndex(), this.pageSize(), this.statusFilter()).subscribe({
            next: (result) => {
                this.logs.set(result.items ?? []);
                this.totalCount.set(result.totalCount ?? 0);
                this.isLoading.set(false);
            },
            error: (err) => {
                console.error('Error loading migration sync logs:', err);
                this.isLoading.set(false);
            }
        });
    }

    onStatusChange(): void {
        this.pageIndex.set(0);
        this.loadLogs();
    }

    nextPage(): void {
        this.pageIndex.update(i => i + 1);
        this.loadLogs();
    }

    prevPage(): void {
        this.pageIndex.update(i => Math.max(0, i - 1));
        this.loadLogs();
    }

    get hasPrevPage(): boolean {
        return this.pageIndex() > 0;
    }

    get hasNextPage(): boolean {
        return (this.pageIndex() + 1) * this.pageSize() < this.totalCount();
    }

    get pageStart(): number {
        return this.pageIndex() * this.pageSize() + 1;
    }

    get pageEnd(): number {
        return Math.min((this.pageIndex() + 1) * this.pageSize(), this.totalCount());
    }
}
